import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, FileText, Megaphone, MonitorPlay, Layers, ShieldCheck, Network, Clock3, Award } from "lucide-react";
import useReveal from "@/hooks/useReveal";

const HERO_IMG =
  "https://images.unsplash.com/photo-1505508520564-7e2642683d77?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8cmVkfDE3Nzc5MDQ0MTB8MA&ixlib=rb-4.1.0&q=85";
const ABOUT_IMG =
  "https://images.unsplash.com/photo-1762526217288-a783242270dc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxuZXclMjBkZWxoaSUyMGNpdHlzY2FwZXxlbnwwfHx8fDE3Nzc5MDQ0MDR8MA&ixlib=rb-4.1.0&q=85";
const SERVICES_IMG =
  "https://images.unsplash.com/photo-1676491409907-b40322daf75d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxibGFuayUyMGJpbGxib2FyZCUyMGNpdHklMjBuaWdodHxlbnwwfHx8fDE3Nzc5MDQ0MDR8MA&ixlib=rb-4.1.0&q=85";
const WHY_IMG =
  "https://images.unsplash.com/photo-1763729805496-b5dbf7f00c79?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGRvY3VtZW50JTIwc2lnbmF0dXJlfGVufDB8fHx8MTc3NzkwNDQwNHww&ixlib=rb-4.1.0&q=85";

const services = [
  {
    icon: FileText,
    title: "Government Tender Execution",
    desc: "End-to-end bidding, compliance, documentation & deployment for central, state and municipal OOH tenders.",
    tag: "01",
  },
  {
    icon: Megaphone,
    title: "Private OOH Campaigns",
    desc: "Concept-to-installation campaigns for brands seeking high-impact outdoor presence across metros & Tier-2.",
    tag: "02",
  },
  {
    icon: MonitorPlay,
    title: "Billboard · Hoarding · Transit",
    desc: "Premium inventory — unipoles, gantries, bus shelters, metro, airports & in-station digital screens.",
    tag: "03",
  },
  {
    icon: Layers,
    title: "End-to-End Campaign Management",
    desc: "Planning, creative, permissions, printing, mounting, monitoring and reporting — one accountable partner.",
    tag: "04",
  },
];

const whyPoints = [
  {
    icon: Award,
    title: "Tender Expertise",
    desc: "Years of navigating government OOH tenders — EMD, technical bids, eligibility & award stages.",
  },
  {
    icon: ShieldCheck,
    title: "Compliance & Documentation",
    desc: "Airtight paperwork: GST, PAN, bid securities, agreements, and municipal clearances.",
  },
  {
    icon: Network,
    title: "Vendor & Network Access",
    desc: "Direct relationships with media owners, printers, fabricators and installation crews Pan-India.",
  },
  {
    icon: Clock3,
    title: "Timely Execution",
    desc: "Hard deadlines respected — campaigns live on time, every time, with monitoring reports.",
  },
];

const industries = [
  { name: "Government", subtitle: "Central · State · Municipal" },
  { name: "Real Estate", subtitle: "Launches · Townships" },
  { name: "FMCG", subtitle: "Go-to-market · Visibility" },
  { name: "Education", subtitle: "Admissions · Branding" },
];

export default function Home() {
  useReveal();

  return (
    <div data-testid="page-home" className="bg-[#0a0a0a] text-white">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative min-h-[92vh] flex items-end overflow-hidden"
      >
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Abstract red geometric architecture"
            className="w-full h-full object-cover opacity-80"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/70 to-[#0a0a0a]/30" />
          <div className="absolute inset-0 grain" />
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 w-full pt-32 pb-16 md:pb-24">
          <div className="flex items-center gap-3 mb-8 reveal">
            <span className="w-10 h-px bg-[#d40202]" />
            <span className="text-xs uppercase tracking-[0.3em] text-[#d40202] font-bold">
              Out-of-Home Advertising · Est. Delhi
            </span>
          </div>

          <h1
            data-testid="hero-heading"
            className="font-display font-black uppercase leading-[0.88] tracking-tighter text-5xl sm:text-7xl md:text-8xl lg:text-[9rem] reveal"
          >
            Outdoor <br />
            <span className="text-[#d40202]">Advertising</span> <br />
            Solutions.
          </h1>

          <p
            data-testid="hero-tagline"
            className="mt-8 max-w-2xl text-base md:text-xl text-zinc-300 leading-relaxed reveal"
          >
            Specialists in <span className="text-white font-semibold">government &amp; private tenders</span> for
            high-impact out-of-home advertising — hoardings, transit, billboards and integrated campaigns across
            India.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4 reveal">
            <Link
              to="/#services"
              data-testid="hero-cta-services"
              className="inline-flex items-center justify-center gap-3 bg-[#d40202] hover:bg-white hover:text-black text-white px-8 py-5 text-xs uppercase tracking-[0.2em] font-bold transition-colors"
            >
              View Services <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-cta-contact"
              className="inline-flex items-center justify-center gap-3 border border-white/30 hover:border-white hover:bg-white hover:text-black text-white px-8 py-5 text-xs uppercase tracking-[0.2em] font-bold transition-colors"
            >
              Contact Us <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
            </Link>
          </div>

          {/* Stats */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10 reveal">
            {[
              { k: "Pan-India", v: "Operations" },
              { k: "Govt + Pvt", v: "Tenders" },
              { k: "24/7", v: "Campaign Monitoring" },
              { k: "100%", v: "Compliance" },
            ].map((s) => (
              <div
                key={s.k}
                className="bg-[#0a0a0a] p-6 md:p-8"
              >
                <div className="font-display font-black text-2xl md:text-4xl text-white">{s.k}</div>
                <div className="mt-2 text-xs uppercase tracking-[0.2em] text-zinc-400">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section
        id="about"
        data-testid="about-section"
        className="relative py-24 md:py-32 border-t border-white/10"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-5 reveal">
            <div className="relative aspect-[4/5] overflow-hidden border border-white/10">
              <img
                src={ABOUT_IMG}
                alt="India Gate New Delhi illuminated at night"
                className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a]/60 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="text-xs uppercase tracking-[0.3em] text-[#d40202] font-bold">HQ / New Delhi</div>
                <div className="mt-2 font-display font-bold text-2xl">Pan-India Reach</div>
              </div>
            </div>
          </div>

          <div className="md:col-span-7 reveal">
            <div className="text-xs uppercase tracking-[0.3em] text-[#d40202] font-bold">/ Who We Are</div>
            <h2 className="mt-6 font-display font-black uppercase leading-[0.95] tracking-tighter text-4xl md:text-6xl">
              An OOH partner <br /> built for <span className="text-[#d40202]">tenders.</span>
            </h2>
            <p className="mt-8 text-zinc-300 text-lg leading-relaxed max-w-2xl">
              AdVeda Advertising is an out-of-home specialist with deep experience in
              government and private sector tenders. From preparing airtight bid documents to
              executing multi-city campaigns — we handle every checkpoint of the tender lifecycle
              so your brand or department ships on time.
            </p>
            <p className="mt-6 text-zinc-400 leading-relaxed max-w-2xl">
              Headquartered in New Delhi with Pan-India operations, we combine compliance,
              creative production, and a tight vendor network to deliver high-impact OOH
              where eyeballs actually live — along highways, metros, transit hubs and urban streets.
            </p>

            <div className="mt-10 grid grid-cols-2 gap-6 max-w-lg">
              <div className="border-l-2 border-[#d40202] pl-4">
                <div className="font-display font-black text-3xl">Govt</div>
                <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mt-1">Tender Specialists</div>
              </div>
              <div className="border-l-2 border-[#d40202] pl-4">
                <div className="font-display font-black text-3xl">Private</div>
                <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mt-1">Brand Campaigns</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section
        id="services"
        data-testid="services-section"
        className="relative py-24 md:py-32 border-t border-white/10 bg-[#060606]"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16 reveal">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-[#d40202] font-bold">/ What We Do</div>
              <h2 className="mt-6 font-display font-black uppercase leading-[0.95] tracking-tighter text-4xl md:text-6xl max-w-3xl">
                Services <span className="text-[#d40202]">engineered</span> for outcomes.
              </h2>
            </div>
            <p className="text-zinc-400 max-w-md text-sm md:text-base leading-relaxed">
              Four tightly integrated service lines — from tender paperwork to a live
              billboard on a national highway. One accountable team.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10 reveal">
            {services.map((s) => (
              <Link
                key={s.title}
                to="/contact#inquiry-form"
                data-testid={`service-card-${s.tag}`}
                className="group bg-[#0a0a0a] p-8 md:p-12 hover:bg-[#111] transition-colors relative block"
              >
                <div className="flex items-start justify-between gap-6">
                  <s.icon className="w-10 h-10 text-[#d40202] shrink-0" strokeWidth={1.5} />
                  <span className="font-display font-black text-5xl text-white/10 group-hover:text-[#d40202]/30 transition-colors">
                    {s.tag}
                  </span>
                </div>
                <h3 className="mt-8 font-display font-bold text-2xl md:text-3xl tracking-tight">
                  {s.title}
                </h3>
                <p className="mt-4 text-zinc-400 leading-relaxed max-w-md">{s.desc}</p>
                <span className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-white group-hover:text-[#d40202] transition-colors">
                  Enquire <ArrowUpRight className="w-4 h-4" />
                </span>
              </Link>
            ))}
          </div>

          <div className="mt-10 relative aspect-[21/9] overflow-hidden border border-white/10 reveal">
            <img
              src={SERVICES_IMG}
              alt="City street with advertising spaces at night"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/30 to-transparent" />
            <div className="absolute inset-0 flex items-center">
              <div className="px-8 md:px-16 max-w-2xl">
                <div className="text-xs uppercase tracking-[0.3em] text-[#d40202] font-bold">/ Coverage</div>
                <h3 className="mt-4 font-display font-black text-3xl md:text-5xl uppercase leading-[0.95] tracking-tighter">
                  From national highways to neighbourhood streets.
                </h3>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section
        id="why"
        data-testid="why-section"
        className="relative py-24 md:py-32 border-t border-white/10"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-5 reveal md:sticky md:top-32 self-start">
            <div className="text-xs uppercase tracking-[0.3em] text-[#d40202] font-bold">/ Why AdVeda</div>
            <h2 className="mt-6 font-display font-black uppercase leading-[0.95] tracking-tighter text-4xl md:text-6xl">
              Four reasons <br />clients <span className="text-[#d40202]">stay.</span>
            </h2>
            <p className="mt-6 text-zinc-400 leading-relaxed max-w-md">
              Tenders are won on detail and campaigns are won on timing. We obsess over both.
            </p>

            <div className="mt-10 hidden md:block relative aspect-[4/3] border border-white/10 overflow-hidden">
              <img
                src={WHY_IMG}
                alt="Man signing business document"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] to-transparent" />
            </div>
          </div>

          <div className="md:col-span-7 reveal">
            <ul className="divide-y divide-white/10 border-y border-white/10">
              {whyPoints.map((p, i) => (
                <li
                  key={p.title}
                  data-testid={`why-item-${i + 1}`}
                  className="group py-8 md:py-10 grid grid-cols-12 gap-6 items-start hover:bg-white/[0.02] transition-colors px-2"
                >
                  <div className="col-span-2 md:col-span-1 font-display font-black text-xl text-[#d40202]">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="col-span-10 md:col-span-11">
                    <div className="flex items-center gap-4">
                      <p.icon className="w-5 h-5 text-[#d40202]" strokeWidth={1.75} />
                      <h3 className="font-display font-bold text-xl md:text-2xl group-hover:text-[#d40202] transition-colors">
                        {p.title}
                      </h3>
                    </div>
                    <p className="mt-3 text-zinc-400 leading-relaxed max-w-xl">{p.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section
        id="industries"
        data-testid="industries-section"
        className="relative py-24 md:py-32 border-t border-white/10 bg-[#060606] overflow-hidden"
      >
        {/* Marquee background */}
        <div className="absolute inset-x-0 top-10 overflow-hidden pointer-events-none select-none" aria-hidden>
          <div className="marquee-track flex whitespace-nowrap">
            {[0, 1].map((k) => (
              <div key={k} className="flex shrink-0">
                {["Government", "Real Estate", "FMCG", "Education", "Transit", "Retail"].map((w, i) => (
                  <span
                    key={`${k}-${i}`}
                    className="font-display font-black uppercase tracking-tighter text-white/[0.04] px-8"
                    style={{ fontSize: "clamp(72px, 14vw, 220px)", lineHeight: 1 }}
                  >
                    {w} <span className="text-[#d40202]/20">·</span>
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="reveal max-w-3xl">
            <div className="text-xs uppercase tracking-[0.3em] text-[#d40202] font-bold">/ Industries</div>
            <h2 className="mt-6 font-display font-black uppercase leading-[0.95] tracking-tighter text-4xl md:text-6xl">
              We work with.
            </h2>
          </div>

          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 reveal">
            {industries.map((ind, i) => (
              <div
                key={ind.name}
                data-testid={`industry-${i + 1}`}
                className="group bg-[#0a0a0a] hover:bg-[#d40202] p-8 md:p-10 transition-colors"
              >
                <div className="text-xs uppercase tracking-[0.3em] text-[#d40202] group-hover:text-black font-bold transition-colors">
                  0{i + 1}
                </div>
                <div className="mt-6 font-display font-black text-3xl md:text-4xl uppercase tracking-tighter group-hover:text-white transition-colors">
                  {ind.name}
                </div>
                <div className="mt-3 text-xs uppercase tracking-[0.2em] text-zinc-500 group-hover:text-white/80 transition-colors">
                  {ind.subtitle}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section
        data-testid="cta-section"
        className="relative py-24 md:py-40 border-t border-white/10"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 text-center reveal">
          <div className="text-xs uppercase tracking-[0.3em] text-[#d40202] font-bold">/ Next Step</div>
          <h2 className="mt-8 font-display font-black uppercase leading-[0.9] tracking-tighter text-5xl md:text-7xl lg:text-8xl max-w-5xl mx-auto">
            Looking to execute a campaign <br />
            or <span className="text-[#d40202]">float a tender?</span>
          </h2>
          <p className="mt-8 text-zinc-400 max-w-xl mx-auto">
            Tell us what you need. We&apos;ll come back with a plan — quickly.
          </p>
          <Link
            to="/contact"
            data-testid="home-final-cta"
            className="mt-12 inline-flex items-center gap-3 bg-[#d40202] hover:bg-white hover:text-black text-white px-10 py-6 text-sm uppercase tracking-[0.2em] font-bold transition-colors"
          >
            Contact AdVeda <ArrowUpRight className="w-5 h-5" strokeWidth={2.5} />
          </Link>
        </div>
      </section>
    </div>
  );
}
